import { Users, Building2, Handshake, CheckSquare, ArrowUpRight, Clock } from 'lucide-react';
import { useDashboardStats } from '../hooks/useDashboard';
import { cn } from '../lib/cn';

interface DashboardViewProps {
  onNavigate: (view: string) => void;
}

const statCards = [
  { key: 'contactsCount' as const, label: 'Contacts', icon: Users, color: 'text-blue-600 bg-blue-50', link: 'contacts' },
  { key: 'organizationsCount' as const, label: 'Organizations', icon: Building2, color: 'text-purple-600 bg-purple-50', link: 'organizations' },
  { key: 'openDeals' as const, label: 'Open Deals', icon: Handshake, color: 'text-green-600 bg-green-50', link: 'deals' },
  { key: 'pendingTasks' as const, label: 'Pending Tasks', icon: CheckSquare, color: 'text-orange-600 bg-orange-50', link: 'tasks' },
];

export function DashboardView({ onNavigate }: DashboardViewProps) {
  const { data, isLoading, error } = useDashboardStats();

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Dashboard</h1>
          <p className="mt-0.5 text-sm text-slate-500">Welcome back. Here is what is happening today.</p>
        </div>
      </div>

      <div className="p-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {statCards.map((card) => {
            const Icon = card.icon;
            const value = data?.[card.key] ?? '--';
            return (
              <button
                key={card.key}
                onClick={() => onNavigate(card.link)}
                className="stat-card group text-left transition-shadow hover:shadow-card-hover"
              >
                <div className="flex items-center justify-between">
                  <div className={cn('rounded-lg p-2', card.color)}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-slate-300 transition-colors group-hover:text-brand-500" />
                </div>
                <div className="mt-3">
                  <div className="text-2xl font-bold text-slate-900">
                    {isLoading ? (
                      <div className="h-8 w-16 animate-pulse rounded bg-surface-200" />
                    ) : (
                      value
                    )}
                  </div>
                  <div className="text-sm text-slate-500">{card.label}</div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Recent Activity */}
        <div className="mt-8">
          <h2 className="mb-4 text-lg font-semibold text-slate-800">Recent Activity</h2>
          <div className="card divide-y divide-surface-100">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-4">
                  <div className="h-8 w-8 animate-pulse rounded-full bg-surface-200" />
                  <div className="flex-1">
                    <div className="h-4 w-48 animate-pulse rounded bg-surface-200" />
                    <div className="mt-1.5 h-3 w-24 animate-pulse rounded bg-surface-100" />
                  </div>
                </div>
              ))
            ) : error ? (
              <div className="px-5 py-8 text-center text-sm text-slate-500">
                Unable to load activity feed.
              </div>
            ) : data?.recentActivity && data.recentActivity.length > 0 ? (
              data.recentActivity.map((item) => (
                <div key={item.id} className="flex items-center gap-4 px-5 py-4">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-surface-100">
                    <Clock className="h-4 w-4 text-slate-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-700">{item.description}</p>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-400">
                      {item.userName && <span>{item.userName}</span>}
                      <span>{new Date(item.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  {item.entityName && (
                    <span className="shrink-0 rounded-full bg-surface-100 px-2.5 py-0.5 text-xs text-slate-600">
                      {item.entityName}
                    </span>
                  )}
                </div>
              ))
            ) : (
              <div className="px-5 py-8 text-center text-sm text-slate-500">
                No recent activity. Start by adding contacts or organizations.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
